import { useEffect, useCallback, type ReactElement } from 'react';
import { Header, type HeaderStepperProps } from '@fz/ui-components-react';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@hooks/useLanguageSwitch';
import { setPathLanguage, extractLanguageFromHref } from '@utils/language-url.util';
import { isRtlLanguage, type Language } from '@models/i18n/language.model';
import { useAppStore, selectCurrentStep, selectIncludeReviewBreadcrumb } from '@store/app.store';
import { useSkywardsSettings } from './useSkywardsSettings';
import { useResponsiveSize } from '@hooks/useResponsiveSize';
import { getResource } from '@utils/resource-translator.util';
import './Header.scss';
const HEADER_LOGO_SRC_RTL = new URL('../../../icons/flydubaiARLogo.svg', import.meta.url).href;
const HEADER_LOGO_SRC_LTR = new URL('../../../icons/iconF.661174c2.svg', import.meta.url).href;

const LANGUAGE_OPTIONS: { code: Language; title: string }[] = [
  { code: 'en', title: 'English' },
  { code: 'ar', title: 'العربية' },
  { code: 'ru', title: 'русский' },
];

const HIDDEN_BREADCRUMB_ROUTES = new Set(['confirmation', 'paymentvalidate']);

const BASE_HEADER_PROPS = {
  navigationItems: [],

  languageItems: {
    items: [],
    selectedLanguage: 'EN',
  },

  skywardsItems: {
    loginLabel: '',
    URLs: [], // Will be filled dynamically
  },

  mobileDrawer: {
    title: 'Menu',
    home: { url: '/' },
  },

  profileItems: {
    isLoggedIn: false,
  },
};

interface HeaderComponentProps {
  showHeaderStepper?: boolean;
}

// eslint-disable-next-line max-lines-per-function
export const HeaderComponent = ({
  showHeaderStepper = true,
}: HeaderComponentProps): ReactElement => {
  const { currentLanguage, switchTo } = useLanguageSwitch();
  const { t } = useTranslation();
  const currentStep = useAppStore(selectCurrentStep);
  // Intercept language anchors from the Header library, including tablet/mobile
  // drawer content that DS portals outside the header DOM tree.
  // Capture on document so preventDefault runs before navigation.
  const handleClick = useCallback(
    (e: MouseEvent): void => {
      const anchor = (e.target as Element).closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href') ?? '';
      const lang = extractLanguageFromHref(href);

      if (!lang) return;

      // Always prevent default for language links — avoids a page reload
      // whether the language is changing or the same one is re-selected.
      e.preventDefault();

      if (lang === currentLanguage) return;

      void switchTo(lang);
    },
    [currentLanguage, switchTo],
  );

  useEffect(() => {
    document.addEventListener('click', handleClick, true);
    return (): void => document.removeEventListener('click', handleClick, true);
  }, [handleClick]);

  const {
    homepageUrl,
    skywardsBenefits,
    skywardsEarning,
    skywardsSpending,
    skywardsMembershipTiers,
    skywardsPartners,
    skywardsFaq,
    skywardsContact,
    skywardsTerms,
  } = useSkywardsSettings();
  const { isDesktop } = useResponsiveSize();
  const headerLogoSrc = isRtlLanguage(currentLanguage) ? HEADER_LOGO_SRC_RTL : HEADER_LOGO_SRC_LTR;
  const includeReviewBreadcrumbFromStore = useAppStore(selectIncludeReviewBreadcrumb);
  const includeReviewBreadcrumb =
    includeReviewBreadcrumbFromStore || globalThis.location.pathname.includes('/review');
  const stepTranslationKeys = [
    'SelectPassenger.PageTitle.Text',
    'PassengerInfo.PageTitle.Text',
    'Review.SeatsTotal.Text',
    'Extras.PageTitle.Text',
    ...(includeReviewBreadcrumb ? ['Review.PageTitle.Text'] : []),
    'Confirmation.PageTitle.Text',
  ];
  const pathSegments = globalThis.location.pathname.split('/').filter(Boolean);
  const routeSegment = pathSegments[pathSegments.length - 1] ?? '';
  const shouldHideBreadcrumb = HIDDEN_BREADCRUMB_ROUTES.has(routeSegment);
  const stepConfigs = stepTranslationKeys.map((translationKey, index) => {
    const stepNumber = index + 1;
    const isCurrentStep = currentStep === stepNumber;
    return {
      stepNumber,
      stepNumberLabelProps: {
        isShown: isDesktop || isCurrentStep,
        variant: isCurrentStep ? 'inverse' : 'inverse-tertiary',
        className: 'label6',
        fontWeightStyle: isCurrentStep ? 'bold' : 'regular',
        translationKey: getResource(translationKey),
      },
      isActive: isCurrentStep,
      isPassed: currentStep > stepNumber,
    };
  });

  const headerStepperProps: HeaderStepperProps | undefined =
    showHeaderStepper && !shouldHideBreadcrumb
      ? {
          currentStep,
          variant: 'olci',
          steps: stepConfigs,
          className: 'olci-header-stepper',
        }
      : undefined;

  const headerProps = {
    ...BASE_HEADER_PROPS,
    skywardsItems: {
      ...BASE_HEADER_PROPS.skywardsItems,
      loginLabel: t('login.Text'),
      URLs: [
        {
          label: t('FFPListBenefits.Text'),
          url: `${homepageUrl}/${currentLanguage}/${skywardsBenefits}`,
        },
        {
          label: t('FFPListEarning.Text'),
          url: `${homepageUrl}/${currentLanguage}/${skywardsEarning}`,
        },
        {
          label: t('FFPListRedeeming.Text'),
          url: `${homepageUrl}/${currentLanguage}/${skywardsSpending}`,
        },
        {
          label: t('FFPListGoldStatus.Text'),
          url: `${homepageUrl}/${currentLanguage}/${skywardsMembershipTiers}`,
        },
        {
          label: t('FFPListPartners.Text'),
          url: `${homepageUrl}/${currentLanguage}/${skywardsPartners}`,
        },
        { label: t('FFPListFAQs.Text'), url: `${homepageUrl}/${currentLanguage}/${skywardsFaq}` },
        {
          label: t('FFPListContact.Text'),
          url: `${homepageUrl}/${currentLanguage}/${skywardsContact}`,
        },
        { label: t('FFPListTC.Text'), url: `${homepageUrl}/${currentLanguage}/${skywardsTerms}` },
      ],
    },
    headerLogo: {
      url: homepageUrl,
      src: headerLogoSrc,
    },
    languageItems: {
      items: LANGUAGE_OPTIONS.map((lang) => ({
        code: lang.code.toUpperCase(),
        title: lang.title,
        url: setPathLanguage(window.location.pathname, lang.code),
        isSelected: currentLanguage.toLowerCase() === lang.code.toLowerCase(),
      })),
      selectedLanguage: currentLanguage.toUpperCase(),
    },
    ...(headerStepperProps ? { headerStepperProps } : {}),
  };

  return (
    <div>
      <Header
        key={`${headerLogoSrc}-${routeSegment}-${currentLanguage}`}
        {...headerProps}
        hideBorder={true}
        showBreadcrumb={!shouldHideBreadcrumb}
        showProgressBar={false}
        containerClassName="header__container"
      />
    </div>
  );
};
